import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Scan, Shield, Zap, Eye, Users, Crown, BookOpen, GraduationCap,
  Briefcase, Search, Cloud, FileDown, Building2, Code, Star, Video,
  FileText, Globe, Lock, CheckCircle, Image, FileVideo, Type, Link,
  Layers, Brain, Package
} from 'lucide-react';
import PageHeader from '../components/PageHeader';

export default function QuienesSomos({ onBack }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-zinc-950 relative">
      <div className="scanlines" />
      <div className="fixed inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 50% 0%, rgba(59, 130, 246, 0.08), transparent 60%)' }} />

      <PageHeader onBack={onBack} />

      <main className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-12 sm:space-y-16">

          {/* ── Hero ── */}
          <div className="text-center space-y-4">
            <p className="font-mono text-xs tracking-widest text-blue-500 uppercase">Quiénes somos</p>
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black tracking-tight">Nuestra misión</h1>
            <p className="text-zinc-400 text-base sm:text-xl max-w-2xl mx-auto leading-relaxed">
              Veritas Scan nació con un objetivo claro: ayudar a personas, profesionales e instituciones a distinguir lo real de lo artificial en la era de la inteligencia artificial generativa.
            </p>
          </div>

          {/* ── Historia ── */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 sm:p-8 space-y-4">
            <h2 className="font-heading text-2xl font-bold">Nuestra historia</h2>
            <p className="text-zinc-400 leading-relaxed">
              Vivimos en un momento histórico en el que las imágenes, vídeos y textos generados por IA son prácticamente indistinguibles de los creados por humanos. Esto plantea desafíos enormes para la confianza, la verdad y la autenticidad en el mundo digital.
            </p>
            <p className="text-zinc-400 leading-relaxed">
              Veritas Scan surge como respuesta a esta necesidad: una herramienta accesible, rápida y transparente que permite a cualquier persona verificar la autenticidad del contenido que consume o recibe. Desde periodistas que necesitan contrastar una imagen antes de publicar, hasta profesores que detectan trabajos generados por IA, pasando por departamentos de RRHH que revisan candidaturas.
            </p>
          </div>

          {/* ── Qué puedes analizar ── */}
          <div>
            <h2 className="font-heading text-2xl font-bold mb-6 sm:mb-8 text-center">¿Qué puedes analizar?</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { icon: Image, color: 'text-blue-400', bg: 'bg-blue-500/10', title: 'Imágenes', desc: 'JPG, PNG, GIF, WebP. Detecta rostros, paisajes y objetos generados por Midjourney, DALL-E, Stable Diffusion y similares. Todas las imágenes pasan por moderación automática antes del análisis.' },
                { icon: FileVideo, color: 'text-red-400', bg: 'bg-red-500/10', title: 'Vídeos', desc: 'MP4, MOV, AVI, WebM, MKV. Detecta deepfakes y vídeos generados por Sora, Runway, Kling o Pika. Análisis fotograma a fotograma con Gemini 2.5 Flash.' },
                { icon: FileText, color: 'text-emerald-400', bg: 'bg-emerald-500/10', title: 'Documentos', desc: 'PDF, DOCX y TXT. Analiza si el texto fue escrito por un humano o generado por ChatGPT, Claude u otros LLMs.' },
                { icon: Type, color: 'text-purple-400', bg: 'bg-purple-500/10', title: 'Texto directo', desc: 'Pega cualquier fragmento de texto y analízalo al instante sin necesidad de subir archivos.' },
                { icon: Globe, color: 'text-cyan-400', bg: 'bg-cyan-500/10', title: 'Páginas web', desc: 'Introduce una URL y analizamos el contenido textual de la página en tiempo real.' },
                { icon: Layers, color: 'text-yellow-400', bg: 'bg-yellow-500/10', title: 'Análisis por lotes', desc: 'Sube hasta 10 archivos a la vez en Pro (50 en Business) y analízalos en paralelo desde la pantalla principal.' },
              ].map(({ icon: Icon, color, bg, title, desc }) => (
                <div key={title} className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-2">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${bg} ${color} mb-1`}><Icon className="w-5 h-5" /></div>
                  <h3 className="font-heading text-base font-bold text-white">{title}</h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Cómo funciona la tecnología ── */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 sm:p-8 space-y-6">
            <h2 className="font-heading text-2xl font-bold">Cómo funciona la tecnología</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
                {
                  icon: Eye, color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20',
                  title: 'Gemini 2.5 Flash',
                  desc: 'Modelo multimodal de Google DeepMind capaz de analizar imágenes, vídeos y texto con razonamiento profundo. Nuestros prompts forenses emplean análisis paso a paso con sesgo escéptico: la carga de la prueba recae en demostrar que el contenido es real.'
                },
                {
                  icon: Shield, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20',
                  title: 'Google Cloud Vision SafeSearch',
                  desc: 'Sistema de moderación automática en dos capas. Todas las imágenes pasan por SafeSearch antes del análisis de IA para detectar y bloquear contenido explícito, violento o ilegal. Tolerancia cero con CSAM.'
                },
                {
                  icon: Cloud, color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/20',
                  title: 'Cloudinary',
                  desc: 'Almacenamiento temporal de imágenes para procesarlas con la API de Gemini y Vision. Las imágenes se eliminan automáticamente tras el análisis, nunca se almacenan de forma permanente.'
                },
                {
                  icon: Lock, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20',
                  title: 'Infraestructura segura',
                  desc: 'Backend en FastAPI (Railway), base de datos MongoDB Atlas con cifrado en tránsito y reposo, frontend en React (Vercel), pagos con Stripe PCI-DSS, autenticación con JWT y Google OAuth 2.0.'
                },
              ].map(({ icon: Icon, color, bg, title, desc }) => (
                <div key={title} className={`p-5 rounded-xl border ${bg} space-y-3`}>
                  <Icon className={`w-6 h-6 ${color}`} />
                  <h4 className="font-heading font-bold text-white">{title}</h4>
                  <p className="text-zinc-400 text-sm leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Casos de uso ── */}
          <div>
            <h2 className="font-heading text-2xl font-bold mb-6 sm:mb-8 text-center">¿Para quién es Veritas Scan?</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: BookOpen, color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20', title: 'Periodistas y medios', desc: 'Verifica la autenticidad de imágenes y artículos antes de publicar. Evita difundir desinformación generada por IA y protege tu credibilidad.' },
                { icon: GraduationCap, color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/20', title: 'Profesores y académicos', desc: 'Detecta trabajos y ensayos generados por ChatGPT u otras IAs. Mantén la integridad académica en tu aula o institución.' },
                { icon: Briefcase, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20', title: 'RRHH y selección', desc: 'Analiza CVs, cartas de presentación y portfolios para identificar candidatos que usan IA sin declararlo.' },
                { icon: Search, color: 'text-orange-400', bg: 'bg-orange-500/10 border-orange-500/20', title: 'Investigadores', desc: 'Estudia la proliferación de contenido artificial en redes sociales y medios digitales con análisis detallados y exportación de informes.' },
                { icon: Building2, color: 'text-pink-400', bg: 'bg-pink-500/10 border-pink-500/20', title: 'Empresas y equipos', desc: 'Con el plan Business, hasta 5 usuarios comparten análisis ilimitados, lotes de 50 archivos, API de 5.000 llamadas/día y soporte prioritario.' },
                { icon: Code, color: 'text-cyan-400', bg: 'bg-cyan-500/10 border-cyan-500/20', title: 'Desarrolladores', desc: 'Integra la detección de IA en tus apps y pipelines mediante la API REST con tu clave personal. Disponible en planes Pro y Business.' },
              ].map(({ icon: Icon, color, bg, title, desc }) => (
                <div key={title} className={`p-5 rounded-xl border ${bg} space-y-3`}>
                  <Icon className={`w-6 h-6 ${color}`} />
                  <h4 className="font-heading font-bold text-white">{title}</h4>
                  <p className="text-zinc-400 text-sm leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Planes ── */}
          <div>
            <h2 className="font-heading text-2xl font-bold mb-6 sm:mb-8 text-center">Planes disponibles</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">

              {/* Free */}
              <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 space-y-4">
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-blue-400" />
                  <h3 className="font-heading text-lg font-bold">Plan Gratuito</h3>
                </div>
                <p className="font-heading text-3xl font-black text-white">Gratis</p>
                <ul className="space-y-2 text-zinc-400 text-sm">
                  {[
                    '5 análisis diarios por dispositivo',
                    'Imágenes, vídeo, documentos, texto y URLs',
                    'Sin necesidad de registro',
                    'Historial local en tu navegador',
                    'Moderación automática de imágenes',
                  ].map(f => (
                    <li key={f} className="flex items-center gap-2">
                      <CheckCircle className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />{f}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Pro */}
              <div className="bg-gradient-to-br from-blue-600/15 to-yellow-600/10 border-2 border-yellow-500/40 rounded-xl p-6 space-y-4 relative">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-yellow-500 text-black font-mono font-black text-xs px-3 py-0.5 rounded-full flex items-center gap-1"><Star className="w-3 h-3" /> Más popular</div>
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-400" />
                  <h3 className="font-heading text-lg font-bold text-yellow-300">Plan Pro</h3>
                </div>
                <div>
                  <span className="font-heading text-3xl font-black text-white">$4.99</span>
                  <span className="text-zinc-400 font-mono text-sm">/mes</span>
                </div>
                <ul className="space-y-2 text-zinc-300 text-sm">
                  {[
                    'Análisis ilimitados sin límite diario',
                    'Análisis de vídeo (MP4, MOV, AVI, WebM, MKV)',
                    'Historial en la nube — sincronizado',
                    'Exportar informes en PDF',
                    'Análisis por lotes — hasta 10 archivos',
                    'API pública — 500 llamadas/día',
                    'Prioridad en la cola de análisis',
                    'Información de sesión activa (IP, ubicación)',
                    'Cancela cuando quieras',
                  ].map(f => (
                    <li key={f} className="flex items-start gap-2">
                      <CheckCircle className="w-3.5 h-3.5 text-yellow-400 flex-shrink-0 mt-0.5" />{f}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Business */}
              <div className="bg-gradient-to-br from-purple-600/15 to-pink-600/10 border-2 border-purple-500/40 rounded-xl p-6 space-y-4">
                <div className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-purple-400" />
                  <h3 className="font-heading text-lg font-bold text-purple-300">Plan Business</h3>
                </div>
                <div>
                  <span className="font-heading text-3xl font-black text-white">$29</span>
                  <span className="text-zinc-400 font-mono text-sm">/mes</span>
                </div>
                <ul className="space-y-2 text-zinc-300 text-sm">
                  {[
                    'Todo lo del plan Pro',
                    'Hasta 5 usuarios incluidos',
                    'Análisis por lotes — hasta 50 archivos',
                    'API pública — 5.000 llamadas/día',
                    'Soporte prioritario por email',
                    'Factura para empresa',
                    'Panel de equipo (próximamente)',
                    'SLA garantizado (próximamente)',
                  ].map(f => (
                    <li key={f} className="flex items-start gap-2">
                      <CheckCircle className="w-3.5 h-3.5 text-purple-400 flex-shrink-0 mt-0.5" />{f}
                    </li>
                  ))}
                </ul>
              </div>

            </div>
          </div>

          {/* ── Funcionalidades Pro/Business ── */}
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 border border-zinc-700 rounded-xl p-6 sm:p-8 space-y-6">
            <h2 className="font-heading text-2xl font-bold">Funcionalidades exclusivas Pro y Business</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
                { icon: Cloud, color: 'text-blue-400', title: 'Historial en la nube', desc: 'Todos tus análisis sincronizados automáticamente. Accesibles desde cualquier dispositivo al iniciar sesión.' },
                { icon: FileDown, color: 'text-emerald-400', title: 'Exportar informes PDF', desc: 'Descarga cada análisis como informe completo: veredicto, confianza, explicación e indicadores detectados.' },
                { icon: FileText, color: 'text-yellow-400', title: 'Análisis por lotes', desc: 'Sube hasta 10 archivos a la vez (50 en Business) y analízalos en paralelo. Los resultados se guardan en el historial.' },
                { icon: Code, color: 'text-purple-400', title: 'API pública', desc: 'Integra Veritas Scan en tus apps. 500 llamadas/día en Pro, 5.000 en Business. Genera tu API key desde el perfil.' },
                { icon: Video, color: 'text-red-400', title: 'Análisis de vídeo', desc: 'Detecta deepfakes y vídeos generados por IA (MP4, MOV, AVI, WebM, MKV) con análisis fotograma a fotograma.' },
                { icon: Shield, color: 'text-cyan-400', title: 'Panel de seguridad', desc: 'Visualiza tu sesión activa: IP, geolocalización aproximada, ISP, navegador y dispositivo. Detecta accesos no autorizados.' },
              ].map(({ icon: Icon, color, title, desc }) => (
                <div key={title} className="flex items-start gap-4">
                  <Icon className={`w-7 h-7 ${color} flex-shrink-0 mt-0.5`} />
                  <div>
                    <h3 className="font-heading font-bold text-white mb-1">{title}</h3>
                    <p className="text-zinc-400 text-sm leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Valores ── */}
          <div>
            <h2 className="font-heading text-2xl font-bold mb-6 sm:mb-8 text-center">Nuestros valores</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
              {[
                { icon: Shield, title: 'Privacidad', desc: 'Los archivos que analizas se eliminan tras el análisis. Las contraseñas se cifran con bcrypt. Los pagos van a través de Stripe PCI-DSS. Nunca vendemos tus datos.', color: 'text-emerald-500' },
                { icon: Zap, title: 'Velocidad', desc: 'Análisis en segundos gracias a Gemini 2.5 Flash, el modelo multimodal de Google. Con moderación en dos capas que no ralentiza el proceso.', color: 'text-blue-500' },
                { icon: Eye, title: 'Transparencia', desc: 'Explicamos siempre el razonamiento detrás de cada veredicto con porcentaje de confianza y detalles concretos. No solo el resultado: el porqué.', color: 'text-purple-500' },
              ].map(({ icon: Icon, title, desc, color }) => (
                <div key={title} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-3">
                  <Icon className={`w-8 h-8 ${color}`} />
                  <h3 className="font-heading text-lg font-bold">{title}</h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Stack técnico ── */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 sm:p-8 space-y-4">
            <h2 className="font-heading text-2xl font-bold">Stack técnico</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                { label: 'IA', value: 'Gemini 2.5 Flash (Google DeepMind)' },
                { label: 'Moderación', value: 'Google Cloud Vision SafeSearch' },
                { label: 'Imágenes', value: 'Cloudinary (temporal)' },
                { label: 'Backend', value: 'FastAPI (Python) en Railway' },
                { label: 'Frontend', value: 'React + Tailwind en Vercel' },
                { label: 'Base de datos', value: 'MongoDB Atlas' },
                { label: 'Pagos', value: 'Stripe (PCI-DSS nivel 1)' },
                { label: 'Auth', value: 'JWT + Google OAuth 2.0' },
                { label: 'Dominio', value: 'veritas-scan.com' },
              ].map(({ label, value }) => (
                <div key={label} className="space-y-0.5">
                  <p className="font-mono text-xs text-zinc-500 uppercase tracking-wider">{label}</p>
                  <p className="text-zinc-200 text-sm font-mono">{value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Blog ── */}
          <div className="bg-gradient-to-r from-blue-600/10 to-purple-600/10 border border-blue-500/25 rounded-xl p-6 sm:p-8 space-y-3">
            <h2 className="font-heading text-2xl font-bold">Blog de Veritas Scan</h2>
            <p className="text-zinc-400 leading-relaxed text-sm sm:text-base">
              Publicamos regularmente artículos sobre detección de contenido IA, tutoriales de uso, noticias del sector y casos de uso reales. El blog cubre categorías como tutoriales, noticias de IA, casos de uso y novedades del producto.
            </p>
            <button onClick={() => navigate('/blog')} className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-mono text-sm uppercase tracking-wider rounded-lg transition-all">
              Ir al blog →
            </button>
          </div>

        </motion.div>
      </main>
    </div>
  );
}
